import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Select, Typography, Row, Col, Statistic, Space, Tag, DatePicker } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import intercompanyService from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Paragraph, Text } = Typography;

const IntercompanyStatementPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { formatCurrency } = useCurrency();
  const [searchParams] = useSearchParams();
  const [affiliateId, setAffiliateId] = useState<string>();
  const [asOf, setAsOf] = useState<Dayjs>(searchParams.get('as_of') ? dayjs(searchParams.get('as_of')) : dayjs());
  const asOfStr = asOf.format('YYYY-MM-DD');

  useEffect(() => {
    const fromQuery = searchParams.get('affiliate');
    if (fromQuery) setAffiliateId(fromQuery);
  }, [searchParams]);

  const { data: affiliates = [] } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const { data: statement, isLoading } = useQuery({
    queryKey: ['intercompany-statement', affiliateId, asOfStr],
    queryFn: () => intercompanyService.statement(affiliateId!, asOfStr),
    enabled: !!affiliateId,
  });

  const directionLabel = (d: string) =>
    d === 'DueFrom' ? t('statusDueFrom') : d === 'DueTo' ? t('statusDueTo') : d;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('intercompanyStatement')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('intercompanyStatementSubtitle')}
          </Paragraph>
        </div>
        <ReportDocumentActions
          reportKey="intercompany-statement"
          params={affiliateId ? { affiliate_company_id: affiliateId, as_of: asOfStr } : null}
        />
      </Space>

      <Space wrap style={{ marginBottom: 16 }} align="end">
        <Space direction="vertical" size={4}>
          <Text type="secondary">{t('affiliate')}</Text>
          <Select
            style={{ width: 360 }}
            placeholder={t('selectAffiliate')}
            value={affiliateId}
            onChange={setAffiliateId}
            options={affiliates.map((a) => ({
              value: a.company_id,
              label: `${a.company_code} — ${a.company_name}`,
            }))}
          />
        </Space>
        <Space direction="vertical" size={4}>
          <Text type="secondary">{t('asAt')}</Text>
          <DatePicker value={asOf} onChange={(d) => d && setAsOf(d)} format="DD-MM-YYYY" allowClear={false} />
        </Space>
      </Space>

      {statement && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={t('dueFrom')}
                  value={statement.due_from_balance}
                  formatter={(v) => formatCurrency(Number(v))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={t('dueTo')}
                  value={statement.due_to_balance}
                  formatter={(v) => formatCurrency(Number(v))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={t('net')}
                  value={statement.net_balance}
                  formatter={(v) => formatCurrency(Number(v))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Space direction="vertical" size={0}>
                  <Typography.Text type="secondary">{t('ageingOpenItemsNet')}</Typography.Text>
                  <Typography.Text>
                    0: {formatCurrency(statement.aging.current)} · 1–30:{' '}
                    {formatCurrency(statement.aging.d1_30)}
                  </Typography.Text>
                  <Typography.Text>
                    31–60: {formatCurrency(statement.aging.d31_60)} · 61–90:{' '}
                    {formatCurrency(statement.aging.d61_90)} · 90+:{' '}
                    {formatCurrency(statement.aging.d90_plus)}
                  </Typography.Text>
                </Space>
              </Card>
            </Col>
          </Row>

          <Card>
            <Table
              loading={isLoading}
              rowKey="id"
              dataSource={statement.lines}
              columns={[
                { title: t('document'), dataIndex: 'document_number' },
                { title: t('type'), dataIndex: 'transaction_type' },
                {
                  title: t('date'),
                  dataIndex: 'transaction_date',
                  render: (d) => dayjs(d).format('DD MMM YYYY'),
                },
                {
                  title: t('direction'),
                  dataIndex: 'direction',
                  render: (d) => (
                    <Tag color={d === 'DueFrom' ? 'green' : 'orange'}>{directionLabel(d)}</Tag>
                  ),
                },
                {
                  title: t('amount'),
                  dataIndex: 'amount',
                  align: 'right',
                  render: (v) => formatCurrency(Number(v)),
                },
                { title: t('days'), dataIndex: 'days_outstanding', align: 'right' },
                { title: tc('status'), dataIndex: 'status' },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default IntercompanyStatementPage;
